import React from "react";

/**
 * SubSegmentedTabs — the SECOND-LEVEL tab primitive used INSIDE a page whose
 * top-level tabs are already `AdminSegmentedTabs` (pill-filled). Introduced
 * in iter101.35 to solve the "main tabs and sub-tabs look identical"
 * problem flagged on Service Setup.
 *
 * Grammar:
 *   Label¹   Label²   Label³   Label⁴
 *   ══════
 *   • Container is a thin bar with a bottom-border-strip (no background,
 *     no card wrapper — sits inline with the section content).
 *   • Active tab: text-foreground + a 2px teal underline sitting on top of
 *     the container's bottom border.
 *   • Inactive tab: text-muted-foreground with hover:text-foreground.
 *   • Count chips are tiny + pill-shaped (same shape as parent primitive
 *     so the two rhyme).
 *
 * Same prop signature as AdminSegmentedTabs so migrating a nested tab bar
 * is a one-import swap.
 *
 * Props:
 *   tabs         — [{ id, label, Icon, count?, hint?, tone? }]
 *   active       — the currently-active tab id.
 *   onChange     — (id) => void.
 *   testid       — data-testid for the container.
 *   tabTestidBase — data-testid prefix per tab button (default: `${testid}-tab`).
 *   showZero     — render the count chip even when count === 0. Default false.
 */
export default function SubSegmentedTabs({
    tabs = [],
    active,
    onChange,
    testid = "sub-segmented-tabs",
    tabTestidBase,
    showZero = false,
    className = "",
}) {
    const testidPrefix = tabTestidBase || `${testid}-tab`;
    return (
        <div
            className={`flex items-end gap-1 border-b border-border overflow-x-auto ${className}`}
            role="tablist"
            data-testid={testid}
        >
            {tabs.map((t) => {
                const on = active === t.id;
                const Icon = t.Icon;
                const hasCount = t.count != null && (t.count > 0 || showZero);
                return (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        onClick={() => onChange?.(t.id)}
                        title={t.hint || undefined}
                        data-testid={`${testidPrefix}-${t.id}`}
                        className={`inline-flex items-center gap-1.5 px-3 pt-2 pb-2 -mb-px text-[12.5px] font-medium whitespace-nowrap transition-all border-b-2 ${
                            on
                                ? "text-foreground border-primary"
                                : "text-muted-foreground hover:text-foreground border-transparent"
                        }`}
                    >
                        {Icon ? <Icon size={13} weight={on ? "fill" : "duotone"} /> : null}
                        {t.label}
                        {hasCount && (
                            <span
                                data-testid={`${testidPrefix}-${t.id}-count`}
                                className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded-full tabular-nums ${
                                    on
                                        ? "bg-primary/15 text-primary"
                                        : t.tone === "danger"
                                            ? "bg-rose-100 text-rose-700"
                                            : t.tone === "warning"
                                                ? "bg-amber-100 text-amber-800"
                                                : "bg-secondary text-muted-foreground"
                                }`}
                            >
                                {t.count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
